import React from "react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";

const EnergyTable = ({
  rawData,
  popoverTexts = {
    //  Default popover texts if not provided
    "Installierte Leistung": {
      "Row Description": [
        "Die installierte Leistung bezeichnet die maximal verfügbare Leistung einer elektrischen Erzeugungsanlage. \n\n", 
        "Zum Vergleich: Ein handelsüblicher Wasserkocher benötigt etwa 2,2 kW (oder 0,0022 MW).",
      ], 
      "Solare Strahlungsenergie":
        "Die maximale Leistung von Solarmodulen wird in Megawatt-Peak (MWp) angegeben. Sie entspricht der Spitzenleistung unter standardisierten Testbedingungen und wird typischerweise bei optimalem Sonnenstand (Peak) erreicht.",
    },
    "Jährliche Produktion": {
      "Row Description":
        "Die jährliche Stromproduktion wird auf Basis der installierten Leistung für ein Kalenderjahr hochgerechnet.\n\n Dabei treffen wir Annahmen zur Anzahl der Betriebsstunden und zur tatsächlich genutzten Erzeugungsleistung. Detaillierte Informationen zur Berechnung finden sich in den Hintergrundinformationen. \n\n Zum Vergleich: Ein 3-Personen-Haushalt benötigt jährlich etwa 3,5 MWh Strom (3.500 kWh oder 0,0035 GWh).",

      "Solare Strahlungsenergie": [
        "Annahmen:",
        "• 8.760 Betriebsstunden pro Jahr",
        "• Nutzungsgrad (Kapazitätsfaktor): 11 %",
      ],

      Windkraft: [
        "Annahmen:",
        "• 8.760 Betriebsstunden pro Jahr",
        "• Nutzungsgrad (Kapazitätsfaktor): 25 %",
      ],

      Biomasse: [
        "Annahmen:",
        "• 8.760 Betriebsstunden pro Jahr",
        "• Nutzungsgrad (Kapazitätsfaktor): 80 %",
      ],

      Wasserkraft: [
        "Annahmen:",
        "• 8.760 Betriebsstunden pro Jahr",
        "• Nutzungsgrad (Kapazitätsfaktor): 50 %",
      ],

      "konventionelle Energie": [
        "Annahmen:",
        "• 8.760 Betriebsstunden pro Jahr",
        "• Nutzungsgrad (Kapazitätsfaktor): 60 %",
      ],
    },
  },
}) => {
  const renderPopoverText = (text) => {
    // If text is a string, convert to array
    const textLines = Array.isArray(text) ? text : [text];

    return textLines.map((line, index) => (
      <div key={index} className="text-sm whitespace-pre-line">
        <span>{line}</span>
      </div>
    ));
  };

  // Function to format numbers to be displayed in the table - part 1 -> 2 decimal places
  const formatNumber = (num) => {
    return num.toLocaleString("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Function to format numbers to be displayed in the table - part 2 -> add MWp, MW, GWh depending on the position in the table
  const formatRowValue = (rowIndex, colIndex, value) => {
    const formattedNumber = formatNumber(value);

    if (rowIndex === 0) {
      // For the first row: first column "MWp", others "MW"
      return colIndex === 0
        ? `${formattedNumber} MWp`
        : `${formattedNumber} MW`;
    } else if (rowIndex === 1) {
      // For the second row: all columns "GWh"
      return `${formattedNumber} GWh`;
    }
  };

  const prepareTableData = () => {
    const getValue = (key, divisor = 1) =>
      (rawData.find((item) => item.name === key)?.value || 0) / divisor;

    return [
      {
        name: "Installierte Leistung",
        values: [
          getValue("PV_Total_Power", 1000),
          getValue("WP_Total_Power", 1000),
          getValue("BM_Total_Power", 1000),
          getValue("HP_Total_Power", 1000),
          getValue("Conventional_Total_Power", 1000),
        ],
        headers: [
          "Solare Strahlungsenergie",
          "Windkraft",
          "Biomasse",
          "Wasserkraft",
          "konventionelle Energie",
        ],
      },
      {
        name: "Jährliche Produktion",
        values: [
          getValue("PV_Annual_Production", 1000000),
          getValue("WP_Annual_Production", 1000000),
          getValue("BM_Annual_Production", 1000000),
          getValue("HP_Annual_Production", 1000000),
          getValue("Conventional_Annual_Production", 1000000),
        ],
        headers: [
          "Solare Strahlungsenergie",
          "Windkraft",
          "Biomasse",
          "Wasserkraft",
          "konventionelle Energie",
        ],
      },
    ];
  };

  const tableData = prepareTableData();

  return (
    <div className="overflow-x-auto rounded-lg p-4">
      <table className="w-full max-w-4xl mx-auto border-collapse text-center min-w-[700px]">
      <caption className="sr-only">Aktuelle Situation der Energieerzeugung in Deutschland</caption>
        <thead>
          <tr className="bg-gray-100">
            <th className="border px-4 py-2"></th>
            <th className="border px-4 py-2">Solare Strahlungsenergie</th>
            <th className="border px-4 py-2">Windkraft</th>
            <th className="border px-4 py-2">Biomasse</th>
            <th className="border px-4 py-2">Wasserkraft</th>
            <th className="border px-4 py-2 bg-gray-300 text-gray-800 mr-2">
              Konventionelle Energie
            </th>
          </tr>
        </thead>
        <tbody>
          {tableData.map((row, rowIndex) => (
            <tr key={rowIndex} className="border">
              <td className="border px-4 py-2 font-semibold min-w-40">
                <div className="flex items-center w-full">
                  <div className="flex-grow text-center">{row.name}</div>
                  <div className="flex items-center justify-center">
                    <Popover className="relative">
                      {({ open }) => (
                        <>
                          <PopoverButton className="focus:outline-none">
                            <FontAwesomeIcon
                              icon={faInfoCircle}
                              className="cursor-pointer text-gray-500"
                            />
                          </PopoverButton>
                          <PopoverPanel
                            className="absolute z-50 w-96 p-4 bg-white shadow-md border rounded-lg text-gray-800"
                            anchor={{ to: "bottom start", gap: 4 }}
                          >
                            <p className="text-sm whitespace-pre-line">
                              {popoverTexts[row.name]?.["Row Description"] || "Keine Informationen verfügbar."}
                            </p>
                          </PopoverPanel>
                        </>
                      )}
                    </Popover>
                  </div>
                </div>
              </td>
              {row.values.map((value, colIndex) => (
                <td
                  key={colIndex}
                  className={`border px-4 py-2 min-w-40 ${
                    colIndex === row.values.length - 1
                      ? "bg-gray-100 text-gray-800 "
                      : ""
                  }`}
                >
                  <div className="flex items-center w-full">
                    <div className="flex-grow text-center">
                      {formatRowValue(rowIndex, colIndex, value)}
                    </div>
                    {rowIndex === 1 && popoverTexts[row.name] && (
                      <div className="flex items-center justify-right">
                        <Popover className="relative">
                          {({ open }) => (
                            <>
                              <PopoverButton className="focus:outline-none">
                                <FontAwesomeIcon
                                  icon={faInfoCircle}
                                  className="cursor-pointer text-gray-500"
                                />
                              </PopoverButton>
                              <PopoverPanel
                                className="absolute z-50 w-96 p-4 bg-white shadow-md border rounded-lg text-gray-800"
                                anchor={{ to: "bottom start", gap: 4 }}
                              >
                                {renderPopoverText(
                                  popoverTexts[row.name]?.[row.headers[colIndex]] || "Keine Informationen verfügbar."
                                )}
                              </PopoverPanel>
                            </>
                          )}
                        </Popover>
                      </div>
                    )}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default EnergyTable;
